/* Peach State Pottery cart drawer. Built 2026-10-07, adapted from the Pitstop Pro drawer.
   Standalone: does not depend on superstore.js.

   HOW IT WORKS
   - Cart changes go through Shopify's Cart AJAX API, then the drawer HTML is re-fetched
     from Shopify (/cart?section_id=psp-cart-drawer) and swapped in. Prices, discounts and
     gift lines are therefore always exactly what Shopify says.
   - Add to cart: product forms are intercepted in the capture phase, so the theme's
     "added to cart" banner never shows and the shopper stays on the product page.
   - Studio Starter Pack (snippets/psp-gift.liquid) adds and removes gift lines with its
     own fetch calls. Those are watched here, so the drawer refreshes when a gift lands,
     but a gift add on its own never pops the drawer open.
   - Shipping estimate: asks Shopify for the LIVE shipping rates for this cart and ZIP
     (the same endpoint the full cart page calculator uses), so the number matches
     checkout and updates by itself when your Shopify rates change. The ZIP is shared
     with the product-page estimator through localStorage key psp_ship_zip.
   - Line items are changed by line KEY, never by line number, because the gift script
     can add/remove lines at the same moment and shift the numbering. */
(function () {
  'use strict';
  if (window.PSPCart) return;

  var routes = (window.Theme && window.Theme.routes) || {};
  var ROOT = (routes.root_url || '/').replace(/\/$/, '');
  var CART_URL = routes.cart_url || '/cart';
  var ADD_URL = routes.cart_add_url || '/cart/add';
  var CHANGE_URL = routes.cart_change_url || '/cart/change';
  var UPDATE_URL = ROOT + '/cart/update';
  var SECTION_ID = 'psp-cart-drawer';
  var ZIP_KEY = 'psp_ship_zip';
  var PHONE_TEXT = '(470) 264-3563';
  var PHONE_HREF = 'tel:+14702643563';
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,textarea,[tabindex]:not([tabindex="-1"])';

  /* Captured at load. layout/theme.liquid renders the gift script inline before this deferred
     file runs, so this is the gift script's wrapper: our own adds still reach it (and it adds
     the free gift), while our observer below never sees our own requests. */
  var nativeFetch = window.fetch.bind(window);

  function $(s, r) { return (r || document).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function isCartPage() { return document.body.classList.contains('template-cart'); }
  function wait(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }
  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /* ---------------------------------------------------------------- ZIP -> state
     Same table as assets/psp-shipping-estimator.js. Contiguous US only: Alaska, Hawaii,
     territories and military ZIPs fall through to the "call us" message, matching the
     shipping profiles. */
  var ZIP_RANGES = [
    [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'], [50, 59, 'VT'],
    [60, 69, 'CT'], [70, 89, 'NJ'], [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'],
    [200, 200, 'DC'], [201, 201, 'VA'], [202, 205, 'DC'], [206, 219, 'MD'], [220, 246, 'VA'],
    [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'], [320, 339, 'FL'],
    [341, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'],
    [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'], [500, 528, 'IA'],
    [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'], [580, 588, 'ND'], [590, 599, 'MT'],
    [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'], [700, 714, 'LA'],
    [716, 729, 'AR'], [730, 732, 'OK'], [733, 733, 'TX'], [734, 749, 'OK'], [750, 799, 'TX'],
    [800, 816, 'CO'], [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'],
    [870, 884, 'NM'], [885, 885, 'TX'], [889, 898, 'NV'], [900, 961, 'CA'], [970, 979, 'OR'],
    [980, 994, 'WA']
  ];
  var STATE_NAMES = {
    AL: 'Alabama', AR: 'Arkansas', AZ: 'Arizona', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
    DC: 'District of Columbia', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', IA: 'Iowa', ID: 'Idaho',
    IL: 'Illinois', IN: 'Indiana', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', MA: 'Massachusetts',
    MD: 'Maryland', ME: 'Maine', MI: 'Michigan', MN: 'Minnesota', MO: 'Missouri', MS: 'Mississippi',
    MT: 'Montana', NC: 'North Carolina', ND: 'North Dakota', NE: 'Nebraska', NH: 'New Hampshire',
    NJ: 'New Jersey', NM: 'New Mexico', NV: 'Nevada', NY: 'New York', OH: 'Ohio', OK: 'Oklahoma',
    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VA: 'Virginia', VT: 'Vermont', WA: 'Washington',
    WI: 'Wisconsin', WV: 'West Virginia', WY: 'Wyoming'
  };
  function zipToState(zip) {
    var n = parseInt(String(zip).slice(0, 3), 10);
    for (var i = 0; i < ZIP_RANGES.length; i++) {
      if (n >= ZIP_RANGES[i][0] && n <= ZIP_RANGES[i][1]) return ZIP_RANGES[i][2];
    }
    return null;
  }
  function loadZip() { try { return localStorage.getItem(ZIP_KEY) || ''; } catch (e) { return ''; } }
  function saveZip(z) { try { localStorage.setItem(ZIP_KEY, z); } catch (e) {} }

  /* ---------------------------------------------------------------- state
     Everything the drawer must remember across a re-render lives here, because the
     section HTML is replaced wholesale on every cart change. */
  var ui = {
    discountOpen: false,
    discountMsg: null,      // { text, kind }
    flash: '',
    zipEditing: false
  };
  var est = {
    zip: loadZip(),
    st: null,
    status: 'idle',         // idle | loading | ok | invalid | noservice | fail
    rate: null,             // { name, cents, options }
    msg: '',
    req: 0,
    sig: ''                 // cart signature the current rate was calculated for
  };

  /* ---------------------------------------------------------------- drawer */
  var Drawer = {
    el: function () { return $('[data-psp-drawer]'); },
    isOpen: function () { var el = Drawer.el(); return !!(el && el.classList.contains('is-open')); },
    open: function (opener) {
      var el = Drawer.el();
      if (!el || isCartPage()) return;
      if (!Drawer.isOpen()) el._opener = opener || document.activeElement;
      // Pick up a ZIP typed into the product-page estimator since the last open.
      var z = loadZip();
      if (z && z !== est.zip) { est.zip = z; est.st = zipToState(z); est.status = 'idle'; renderEst(); }
      el.setAttribute('aria-hidden', 'false');
      el.classList.add('is-open');
      document.documentElement.classList.add('psp-no-scroll');
      document.body.classList.add('psp-no-scroll');
      setTimeout(function () {
        var close = $('.psp-drawer__close', el);
        if (close && !el.contains(document.activeElement)) close.focus({ preventScroll: true });
      }, 60);
      maybeEstimate();
    },
    close: function () {
      var el = Drawer.el();
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('psp-no-scroll');
      document.body.classList.remove('psp-no-scroll');
      if (el._opener && el._opener.focus && document.contains(el._opener)) {
        try { el._opener.focus({ preventScroll: true }); } catch (e) {}
      }
    }
  };

  function cartState() {
    var s = $('[data-psp-drawer] [data-psp-cart-state]');
    if (!s) return { count: 0, total: 0, ship: false };
    return {
      count: parseInt(s.getAttribute('data-item-count'), 10) || 0,
      total: parseInt(s.getAttribute('data-total'), 10) || 0,
      ship: s.getAttribute('data-requires-shipping') === 'true'
    };
  }

  function updateHeaderCount(count) {
    $$('[data-header-cart-count]').forEach(function (el) {
      el.setAttribute('data-header-cart-count', count > 0 ? count : '');
      el.classList.toggle('visible', count > 0);
    });
    $$('.site-header-cart-button').forEach(function (btn) { btn.classList.toggle('count-hidden', count === 0); });
  }

  /* Re-apply everything that lives in JS state onto freshly rendered HTML. */
  function afterRender() {
    var st = cartState();
    updateHeaderCount(st.count);

    var flash = $('[data-psp-flash]');
    if (flash) { flash.textContent = ui.flash; flash.hidden = !ui.flash; }

    var form = $('[data-psp-discount-form]');
    var tog = $('[data-psp-discount-toggle]');
    if (form) form.hidden = !ui.discountOpen;
    if (tog) tog.setAttribute('aria-expanded', ui.discountOpen ? 'true' : 'false');
    var dm = $('[data-psp-discount-msg]');
    if (dm) {
      dm.textContent = ui.discountMsg ? ui.discountMsg.text : '';
      dm.className = 'psp-discount__msg' + (ui.discountMsg ? ' is-' + ui.discountMsg.kind : '');
    }

    renderEst();
    document.dispatchEvent(new CustomEvent('psp:cart:updated', { detail: st }));
  }

  var refreshing = null, refreshAgain = false;
  function refresh() {
    if (refreshing) { refreshAgain = true; return refreshing; }
    var cur = Drawer.el();
    if (!cur) return Promise.resolve();
    var focusId = document.activeElement && cur.contains(document.activeElement)
      ? document.activeElement.getAttribute('data-focus-id') : null;
    refreshing = nativeFetch(CART_URL + '?section_id=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-psp-drawer]');
        var el = Drawer.el();
        if (fresh && el) {
          el.innerHTML = fresh.innerHTML;
          if (focusId) {
            var f = el.querySelector('[data-focus-id="' + focusId + '"]') || el.querySelector('.psp-drawer__close');
            if (f) { try { f.focus({ preventScroll: true }); } catch (e) {} }
          }
        }
        afterRender();
        maybeEstimate();
      })
      .catch(function () {})
      .then(function () {
        refreshing = null;
        if (refreshAgain) { refreshAgain = false; return refresh(); }
      });
    return refreshing;
  }

  function setFlash(text) {
    ui.flash = text || '';
    var flash = $('[data-psp-flash]');
    if (flash) { flash.textContent = ui.flash; flash.hidden = !ui.flash; }
    if (text) setTimeout(function () { if (ui.flash === text) setFlash(''); }, 6000);
  }

  function readError(r, fallback) {
    return r.json().catch(function () { return {}; }).then(function (d) {
      throw new Error(d.description || d.message || fallback);
    });
  }

  /* ---------------------------------------------------------------- cart API */
  var Cart = {
    add: function (body) {
      return nativeFetch(ADD_URL + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      }).then(function (r) {
        if (!r.ok) return readError(r, 'Could not add to cart.');
        return r.json();
      }).then(function (d) { return refresh().then(function () { return d; }); });
    },
    change: function (key, quantity) {
      return nativeFetch(CHANGE_URL + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) {
        if (!r.ok) return readError(r, 'Could not update the cart.');
        return r.json();
      }).then(function () {
        return refresh();
      }).then(function () {
        if (isCartPage()) window.location.reload();
      });
    }
  };

  var qtyTimers = {};
  function lineFor(el) { return el.closest('[data-psp-line]'); }
  function queueQty(line, qty) {
    var key = line.getAttribute('data-key');
    clearTimeout(qtyTimers[key]);
    qtyTimers[key] = setTimeout(function () {
      line.classList.add('is-busy');
      Cart.change(key, qty).catch(function (err) {
        setFlash(err.message);
        refresh();
      });
    }, 350);
  }

  /* ---------------------------------------------------------------- shipping estimate */
  function cartSig() {
    var s = cartState();
    var keys = $$('[data-psp-drawer] [data-psp-line]').map(function (l) {
      var q = $('[data-psp-qty-input]', l);
      return l.getAttribute('data-key') + ':' + (q ? q.value : 'g');
    }).join('|');
    return s.total + '#' + keys;
  }

  function addrQS(zip, st) {
    return 'shipping_address%5Bzip%5D=' + encodeURIComponent(zip) +
      '&shipping_address%5Bcountry%5D=' + encodeURIComponent('United States') +
      '&shipping_address%5Bprovince%5D=' + encodeURIComponent(STATE_NAMES[st]);
  }
  function badAddress(d) {
    var e = new Error('bad address');
    e.badAddress = true; e.detail = d;
    throw e;
  }
  function pollRates(qs, n) {
    return nativeFetch(ROOT + '/cart/async_shipping_rates.json?' + qs, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (r.status === 422) return r.json().catch(function () { return {}; }).then(badAddress);
        if (!r.ok) throw new Error('rates ' + r.status);
        return r.text();
      })
      .then(function (t) {
        var d = null;
        try { d = t ? JSON.parse(t) : null; } catch (e) { d = null; }
        if (d && d.shipping_rates) return d.shipping_rates;
        if (n >= 14) throw new Error('timeout');
        return wait(n < 4 ? 400 : 800).then(function () { return pollRates(qs, n + 1); });
      });
  }
  function syncRates(qs) {
    return nativeFetch(ROOT + '/cart/shipping_rates.json?' + qs, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (r.status === 422) return r.json().catch(function () { return {}; }).then(badAddress);
        if (!r.ok) throw new Error('rates ' + r.status);
        return r.json();
      })
      .then(function (d) { return (d && d.shipping_rates) || []; });
  }
  function fetchRates(zip, st) {
    var qs = addrQS(zip, st);
    return nativeFetch(ROOT + '/cart/prepare_shipping_rates.json?' + qs, {
      method: 'POST', credentials: 'same-origin', headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (r.status === 422) return r.json().catch(function () { return {}; }).then(badAddress);
        if (!r.ok) throw new Error('prepare ' + r.status);
        return pollRates(qs, 0);
      })
      .catch(function (e) {
        if (e && e.badAddress) throw e;
        return syncRates(qs);
      });
  }

  function estimate(zip, fromUser) {
    zip = String(zip || '').replace(/\D/g, '').slice(0, 5);
    if (!/^\d{5}$/.test(zip)) {
      if (fromUser) { est.status = 'invalid'; est.msg = 'Please enter a 5-digit ZIP code.'; renderEst(); }
      return;
    }
    var st = zipToState(zip);
    est.zip = zip; est.st = st;
    saveZip(zip);
    ui.zipEditing = false;
    if (!st) { est.status = 'noservice'; est.rate = null; est.sig = cartSig(); renderEst(); return; }

    var id = ++est.req;
    est.status = 'loading';
    est.sig = cartSig();
    renderEst();

    fetchRates(zip, st).then(function (rates) {
      if (id !== est.req) return;
      if (!rates || !rates.length) { est.status = 'noservice'; est.rate = null; renderEst(); return; }
      var best = rates.slice().sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); })[0];
      est.rate = { name: best.name || 'Shipping', cents: Math.round(parseFloat(best.price) * 100), options: rates.length };
      est.status = 'ok';
      renderEst();
    }).catch(function (err) {
      if (id !== est.req) return;
      est.rate = null;
      if (err && err.badAddress) {
        est.status = 'invalid';
        est.msg = 'We couldn’t find that ZIP code. Please double-check it.';
        ui.zipEditing = true;
      } else {
        est.status = 'fail';
      }
      renderEst();
    });
  }

  var estTimer = null;
  /* Re-run the estimate whenever the cart has changed since the last one, but only for a
     shopper who has given us a ZIP, and only while the drawer is open. */
  function maybeEstimate() {
    if (!Drawer.isOpen() || !est.zip || !cartState().ship || !cartState().count) return;
    if (est.status === 'invalid') return;
    if (est.status !== 'idle' && est.sig === cartSig()) return;
    clearTimeout(estTimer);
    estTimer = setTimeout(function () { estimate(est.zip, false); }, 250);
  }

  function renderEst() {
    var box = $('[data-psp-drawer] [data-psp-est]');
    var totalEl = $('[data-psp-drawer] [data-psp-total]');
    var noteEl = $('[data-psp-drawer] [data-psp-total-note]');
    var st = cartState();
    if (!box) {
      if (totalEl) totalEl.textContent = money(st.total);
      return;
    }
    var label = $('[data-psp-est-label]', box);
    var value = $('[data-psp-est-value]', box);
    var change = $('[data-psp-est-change]', box);
    var form = $('[data-psp-zip-form]', box);
    var input = $('[data-psp-zip-input]', box);
    var msg = $('[data-psp-est-msg]', box);

    if (input && document.activeElement !== input) input.value = est.zip || '';

    var place = est.st ? STATE_NAMES[est.st] + ' ' + est.zip : est.zip;
    var showForm = ui.zipEditing || est.status === 'idle' || est.status === 'invalid' || est.status === 'noservice';
    form.hidden = !showForm;
    change.hidden = showForm;
    box.classList.toggle('is-loading', est.status === 'loading');
    msg.className = 'psp-est__msg';
    msg.innerHTML = '';

    var total = st.total;
    var note = 'Plus shipping. Enter your ZIP to see the delivered total.';
    var taxNote = est.st === 'GA' ? 'Georgia sales tax is added at checkout.' : 'No sales tax outside Georgia.';

    switch (est.status) {
      case 'loading':
        label.textContent = 'Shipping to ' + place;
        value.innerHTML = '<span class="psp-spinner" aria-hidden="true"></span>Calculating';
        note = 'Getting your live freight rate…';
        break;
      case 'ok':
        label.textContent = 'Shipping to ' + place;
        value.textContent = est.rate.cents === 0 ? 'FREE' : money(est.rate.cents);
        total = st.total + est.rate.cents;
        msg.textContent = est.rate.name + (est.rate.options > 1 ? ' · other options at checkout' : '');
        msg.classList.add('is-info');
        note = 'Includes shipping. ' + taxNote;
        break;
      case 'noservice':
        label.textContent = est.zip ? 'Shipping to ' + est.zip : 'Shipping';
        value.textContent = 'Custom quote';
        msg.innerHTML = 'That ZIP is outside our flat-rate area (Alaska, Hawaii, territories and military addresses). ' +
          '<a href="' + PHONE_HREF + '">Call or text ' + PHONE_TEXT + '</a> and we’ll get you a delivered price.';
        msg.classList.add('is-error');
        break;
      case 'invalid':
        label.textContent = 'Shipping';
        value.textContent = 'Enter ZIP';
        msg.textContent = est.msg;
        msg.classList.add('is-error');
        break;
      case 'fail':
        label.textContent = 'Shipping to ' + place;
        value.textContent = 'At checkout';
        msg.innerHTML = 'Live rates didn’t load just now. Your exact freight shows at checkout, or <a href="' +
          PHONE_HREF + '">call ' + PHONE_TEXT + '</a>.';
        msg.classList.add('is-error');
        break;
      default:
        label.textContent = 'Shipping';
        value.textContent = 'Enter ZIP';
    }

    if (totalEl) totalEl.textContent = money(total);
    if (noteEl) noteEl.textContent = note;
    var foot = $('[data-psp-drawer] .psp-drawer__foot');
    if (foot) foot.classList.toggle('has-ship', est.status === 'ok');
  }

  /* ---------------------------------------------------------------- discounts
     POST /cart/update.js {discount} applies the code to the cart (Shopify says whether it
     applies and the totals update); GET /discount/CODE also sets the checkout cookie. */
  function currentCodes(cart) {
    return (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
  }
  function postDiscount(codes) {
    return nativeFetch(UPDATE_URL + '.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(function (r) { return r.json(); });
  }
  function applyDiscount(code) {
    code = (code || '').trim().toUpperCase();
    if (!code) return Promise.resolve(false);
    return nativeFetch(CART_URL + '.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = currentCodes(cart);
        if (codes.indexOf(code) === -1) codes.push(code);
        return postDiscount(codes);
      })
      .then(function (cart) {
        var hit = (cart.discount_codes || []).filter(function (d) { return d.code.toUpperCase() === code; })[0];
        var ok = !!(hit && hit.applicable);
        if (ok) { try { nativeFetch(ROOT + '/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }); } catch (e) {} }
        ui.discountMsg = ok
          ? { text: 'Code ' + code + ' applied. It carries through to checkout.', kind: 'ok' }
          : { text: 'That code isn’t valid for this cart.', kind: 'error' };
        if (ok) ui.discountOpen = false;
        return refresh().then(function () {
          var input = $('[data-psp-discount-input]');
          if (input && !ok) input.value = code;
          return ok;
        });
      });
  }
  function removeDiscount(code) {
    return nativeFetch(CART_URL + '.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
      .then(function (cart) {
        return postDiscount(currentCodes(cart).filter(function (c) { return c.toUpperCase() !== String(code).toUpperCase(); }));
      })
      .then(function () { ui.discountMsg = null; return refresh(); });
  }

  /* ---------------------------------------------------------------- events */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('[data-psp-drawer-close]')) {
      var c = t.closest('[data-psp-drawer-close]');
      if (c.tagName !== 'A') e.preventDefault();
      Drawer.close();
      return;
    }

    // Header cart icon (Superstore's own markup, no header edit needed) or any [data-psp-cart-open].
    var opener = t.closest('.site-header-cart-button, [data-psp-cart-open]');
    if (opener && Drawer.el() && !isCartPage() && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      Drawer.open(opener);
      return;
    }

    var drawer = t.closest('[data-psp-drawer]');
    if (!drawer) return;

    var step = t.closest('[data-psp-qty-minus],[data-psp-qty-plus]');
    if (step) {
      var line = lineFor(step);
      var input = line && $('[data-psp-qty-input]', line);
      if (!input) return;
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + (step.hasAttribute('data-psp-qty-plus') ? 1 : -1));
      input.value = next;
      if (next === 0) line.classList.add('is-busy');
      queueQty(line, next);
      return;
    }

    var rm = t.closest('[data-psp-remove]');
    if (rm) {
      e.preventDefault();
      var l = lineFor(rm);
      l.classList.add('is-busy');
      Cart.change(l.getAttribute('data-key'), 0).catch(function (err) { setFlash(err.message); refresh(); });
      return;
    }

    if (t.closest('[data-psp-discount-toggle]')) {
      ui.discountOpen = !ui.discountOpen;
      afterRender();
      if (ui.discountOpen) { var di = $('[data-psp-discount-input]'); if (di) di.focus(); }
      return;
    }

    var dr = t.closest('[data-psp-discount-remove]');
    if (dr) {
      e.preventDefault();
      removeDiscount(dr.getAttribute('data-psp-discount-remove')).catch(function () { refresh(); });
      return;
    }

    if (t.closest('[data-psp-est-change]')) {
      ui.zipEditing = true;
      renderEst();
      var zi = $('[data-psp-zip-input]');
      if (zi) { zi.focus(); zi.select(); }
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest && e.target.closest('[data-psp-drawer] [data-psp-qty-input]');
    if (!input) return;
    queueQty(lineFor(input), Math.max(0, parseInt(input.value, 10) || 0));
  });

  document.addEventListener('input', function (e) {
    var z = e.target.closest && e.target.closest('[data-psp-drawer] [data-psp-zip-input]');
    if (!z) return;
    var clean = z.value.replace(/\D/g, '').slice(0, 5);
    if (clean !== z.value) z.value = clean;
  });

  // Drawer forms: ZIP estimate + discount code.
  document.addEventListener('submit', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var zf = t.closest('[data-psp-zip-form]');
    if (zf) {
      e.preventDefault(); e.stopPropagation();
      estimate(($('[data-psp-zip-input]', zf) || {}).value, true);
      return;
    }
    var df = t.closest('[data-psp-discount-form]');
    if (df) {
      e.preventDefault(); e.stopPropagation();
      var input = $('[data-psp-discount-input]', df);
      var btn = $('[type=submit]', df);
      if (!input || !input.value.trim()) {
        ui.discountMsg = { text: 'Enter a discount code.', kind: 'error' };
        afterRender();
        return;
      }
      if (btn) btn.disabled = true;
      applyDiscount(input.value).catch(function () {
        ui.discountMsg = { text: 'Could not apply that code. Please try again.', kind: 'error' };
        afterRender();
      }).then(function () { var b = $('[data-psp-discount-form] [type=submit]'); if (b) b.disabled = false; });
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (!Drawer.isOpen()) return;
    if (e.key === 'Escape') { Drawer.close(); return; }
    if (e.key !== 'Tab') return;
    var box = Drawer.el();
    var f = $$(FOCUSABLE, box).filter(function (x) { return x.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------------------------------------------------------------- add to cart
     Capture phase, so Superstore's own handler and its banner never run. The whole form
     is posted: variant, quantity, Globo option properties and selling plans go through
     exactly as the theme would have sent them. A form carrying a file upload is left to
     the theme, since files can't go through the AJAX add. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
    if (!Drawer.el() || isCartPage()) return;
    var fd = new FormData(form);
    if (!fd.get('id')) return;
    var hasFile = false;
    fd.forEach(function (v) { if (typeof v !== 'string' && v && v.size) hasFile = true; });
    if (hasFile) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var body = new URLSearchParams();
    fd.forEach(function (v, k) { if (typeof v === 'string') body.append(k, v); });
    var btn = (e.submitter && form.contains(e.submitter)) ? e.submitter : form.querySelector('[type=submit],[name=add]');
    if (btn) { btn.disabled = true; btn.classList.add('is-busy'); btn.setAttribute('aria-busy', 'true'); }
    var old = form.querySelector('.psp-atc-error');
    if (old) old.remove();

    Cart.add(body.toString())
      .then(function () { Drawer.open(btn); })
      .catch(function (err) {
        var msg = document.createElement('p');
        msg.className = 'psp-atc-error';
        msg.setAttribute('role', 'alert');
        msg.textContent = err.message || 'Could not add to cart.';
        (btn ? btn.parentNode : form).appendChild(msg);
      })
      .then(function () { if (btn) { btn.disabled = false; btn.classList.remove('is-busy'); btn.removeAttribute('aria-busy'); } });
  }, true);

  /* ---------------------------------------------------------------- outside cart changes
     Apps (Globo add-ons), the theme, and the Studio Starter Pack script change the cart
     with their own fetch/XHR calls. Refresh the drawer after any of them; open it after an
     add, EXCEPT a free-gift add (the gift script also runs on page load). */
  var obsTimer = null, obsOpen = false;
  function observed(url, method, body) {
    if (!url || String(method || 'GET').toUpperCase() === 'GET') return;
    var u = String(url);
    var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(u);
    var isChange = /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(u);
    if (!isAdd && !isChange) return;
    var isGift = typeof body === 'string' && body.indexOf('_psp_gift') !== -1;
    if (isAdd && !isGift) obsOpen = true;
    clearTimeout(obsTimer);
    obsTimer = setTimeout(function () {
      var open = obsOpen; obsOpen = false;
      refresh().then(function () { if (open) Drawer.open(); });
    }, 250);
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url);
    var method = (init && init.method) || (input && input.method) || 'GET';
    var body = init && init.body;
    var p = nativeFetch.apply(null, arguments);
    try { p.then(function (r) { if (r && r.ok) observed(url, method, body); }, function () {}); } catch (e) {}
    return p;
  };
  if (window.XMLHttpRequest) {
    var xo = XMLHttpRequest.prototype.open, xs = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._pspMethod = method; this._pspUrl = url;
      return xo.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      var self = this;
      try {
        this.addEventListener('load', function () {
          if (self.status >= 200 && self.status < 300) observed(self._pspUrl, self._pspMethod, typeof body === 'string' ? body : '');
        });
      } catch (e) {}
      return xs.apply(this, arguments);
    };
  }

  /* Keep the drawer in step when the cart changes in another tab, or the shopper comes back
     to a page restored from the back/forward cache. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  window.addEventListener('storage', function (e) {
    if (e.key === ZIP_KEY && e.newValue && e.newValue !== est.zip) { est.zip = e.newValue; est.status = 'idle'; renderEst(); maybeEstimate(); }
  });

  function init() { afterRender(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.PSPCart = {
    open: Drawer.open,
    close: Drawer.close,
    refresh: refresh,
    add: Cart.add,
    change: Cart.change,
    estimate: function (zip) { return estimate(zip, true); },
    applyDiscount: applyDiscount,
    removeDiscount: removeDiscount
  };
})();
